import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SignOut, FloppyDisk, Trash, Plus, ShieldCheck, Info } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth, roleLabel } from "../context/AuthContext";
import { useContent } from "../context/ContentContext";
import { CONTENT_GROUPS } from "../data/contentGroups";
import { ImageField } from "../components/ImageField";
import { UsersPanel } from "../components/admin/UsersPanel";
import { ReviewPanel } from "../components/admin/ReviewPanel";
import { HistoryPanel } from "../components/admin/HistoryPanel";
import { AuditPanel } from "../components/admin/AuditPanel";
import { PoliciesPanel } from "../components/admin/PoliciesPanel";
import { PlayersPanel } from "../components/admin/PlayersPanel";
import { TeamEntryPanel } from "../components/admin/TeamEntryPanel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";

const field = "rounded-none border-navy/20 bg-white focus-visible:ring-2 focus-visible:ring-gold focus-visible:border-gold";

const RoleBanner = () => {
  const { user } = useAuth();
  if (user?.role === "editor_review") {
    return (
      <div className="flex items-start gap-3 bg-gold/15 border border-gold/40 p-4 mb-6 text-sm text-navy" data-testid="review-banner">
        <Info size={20} className="text-gold shrink-0 mt-0.5" />
        <span>Your changes are <strong>submitted for master approval</strong> and won't appear on the live site until approved.</span>
      </div>
    );
  }
  return null;
};

function ContentEditor() {
  const { content, reload } = useContent();
  const { user } = useAuth();
  const [draft, setDraft] = useState(content);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(content); }, [content]);
  const set = (key, val) => setDraft((d) => ({ ...d, [key]: val }));

  const save = async () => {
    const changed = {};
    Object.keys(draft).forEach((k) => { if (draft[k] !== content[k]) changed[k] = draft[k]; });
    if (Object.keys(changed).length === 0) { toast.info("No changes to save"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/save", { content: changed, description: "Updated website content" });
      if (data.pending) toast.success("Changes submitted for master approval");
      else { await reload(); toast.success("Content saved and live"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <RoleBanner />
      <div className="flex justify-between items-center mb-6 sticky top-0 bg-cream py-4 z-10">
        <p className="text-navy/60 text-sm">Edit any text or image below.</p>
        <button onClick={save} disabled={saving} data-testid="save-content-btn" className="inline-flex items-center gap-2 bg-navy text-white font-semibold px-6 py-3 rounded-sm hover:bg-gold hover:text-navy transition-colors disabled:opacity-60">
          <FloppyDisk size={18} /> {saving ? "Saving…" : user?.role === "editor_review" ? "Submit for approval" : "Save changes"}
        </button>
      </div>
      <Accordion type="multiple" defaultValue={["Brand", "Home — Hero"]} className="space-y-3">
        {CONTENT_GROUPS.map((g) => (
          <AccordionItem key={g.group} value={g.group} className="bg-white border border-navy/10 px-5">
            <AccordionTrigger className="font-heading text-lg text-navy hover:no-underline">{g.group}</AccordionTrigger>
            <AccordionContent className="space-y-5 pb-6">
              {g.fields.map((f) => (
                <div key={f.key}>
                  {f.type === "image" ? (
                    <ImageField label={f.label} value={draft[f.key] || ""} onChange={(v) => set(f.key, v)} />
                  ) : (
                    <>
                      <label className="text-sm font-medium text-navy mb-2 block">{f.label}</label>
                      {f.type === "textarea" ? (
                        <Textarea data-testid={`content-${f.key}`} value={draft[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} rows={3} className={field} />
                      ) : (
                        <Input data-testid={`content-${f.key}`} value={draft[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className={field} />
                      )}
                    </>
                  )}
                </div>
              ))}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

function PagesManager() {
  const { settings, content, reload } = useContent();

  const toggle = async (key, val) => {
    try {
      const { data } = await api.post("/admin/save", { settings: { [key]: val }, description: `Toggle ${key}` });
      if (data.pending) toast.success("Change submitted for master approval");
      else { await reload(); toast.success("Updated"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const Row = ({ label, desc, k }) => (
    <div className="flex items-center justify-between py-3 border-b border-navy/10 last:border-0">
      <div>
        <div className="font-medium text-navy">{label}</div>
        <div className="text-sm text-navy/60">{desc}</div>
      </div>
      <Switch data-testid={`toggle-${k}`} checked={!!settings[k]} onCheckedChange={(v) => toggle(k, v)} />
    </div>
  );

  return (
    <div className="space-y-8">
      <RoleBanner />
      <div className="bg-white border border-navy/10 p-6">
        <h3 className="font-heading text-xl text-navy mb-4">Page & feature visibility</h3>
        <Row label="Events page" desc="Show the Events page and nav link." k="events_enabled" />
        <Row label="News page" desc="Show the News page and nav link." k="news_enabled" />
        <Row label="Facebook link" desc={content.facebook_url ? `Show Facebook link (${content.facebook_url})` : "Add a Facebook URL under Content → Social & Links first."} k="facebook_enabled" />
        <Row label="Partner / Sponsor section" desc="Show the 'Proudly partnering with' section on the home page." k="partner_enabled" />
      </div>
      <PostManager type="events" title="Events" />
      <PostManager type="news" title="News" />
    </div>
  );
}

function PostManager({ type, title }) {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", date: "", image: "", published: true });
  const load = () => api.get(`/admin/${type}`).then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, [type]);

  const add = async () => {
    if (!form.title || !form.body) { toast.error("Title and body required"); return; }
    try {
      await api.post(`/admin/${type}`, form);
      setForm({ title: "", body: "", date: "", image: "", published: true });
      load(); toast.success(`${title} item added`);
    } catch (e) { toast.error("Failed to add"); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/${type}/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="bg-white border border-navy/10 p-6">
      <h3 className="font-heading text-xl text-navy mb-4">Manage {title}</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <Input data-testid={`${type}-title`} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={field} />
        <Input data-testid={`${type}-date`} placeholder="Date (e.g. 12 July 2026)" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={field} />
      </div>
      <Textarea data-testid={`${type}-body`} placeholder="Details" rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={`${field} mt-4`} />
      <div className="mt-4"><ImageField label="Image (optional)" value={form.image} onChange={(v) => setForm({ ...form, image: v })} /></div>
      <button onClick={add} data-testid={`${type}-add-btn`} className="mt-4 inline-flex items-center gap-2 bg-navy text-white font-semibold px-5 py-2.5 rounded-sm hover:bg-gold hover:text-navy transition-colors">
        <Plus size={18} /> Add to {title}
      </button>
      <div className="mt-6 space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="flex items-center justify-between border border-navy/10 px-4 py-3">
            <div>
              <div className="font-medium text-navy">{p.title}</div>
              <div className="text-xs text-navy/50">{p.date}</div>
            </div>
            <button onClick={() => del(p.id)} data-testid={`${type}-delete-${p.id}`} className="text-navy/50 hover:text-red-500 transition-colors"><Trash size={20} /></button>
          </div>
        ))}
        {posts.length === 0 && <p className="text-navy/40 text-sm">No {title.toLowerCase()} yet.</p>}
      </div>
    </div>
  );
}

function Enquiries() {
  const [subs, setSubs] = useState([]);
  useEffect(() => { api.get("/submissions").then((r) => setSubs(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-4">
      {subs.length === 0 && <p className="text-navy/50">No enquiries yet.</p>}
      {subs.map((s) => (
        <div key={s.id} className="bg-white border border-navy/10 p-5" data-testid="enquiry-item">
          <div className="flex flex-wrap justify-between gap-2">
            <span className="font-heading text-lg text-navy">{s.name}</span>
            <span className="text-xs uppercase tracking-wide bg-gold/20 text-navy px-2 py-1 rounded-sm">{s.type}</span>
          </div>
          <div className="text-sm text-navy/60 mt-1">{s.email} · {s.phone || "no phone"} · {new Date(s.created_at).toLocaleString()}</div>
          <p className="mt-3 text-navy/80 whitespace-pre-line">{s.message}</p>
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const { reload } = useContent();
  const navigate = useNavigate();
  const isMaster = user?.role === "master";
  const [pendingCount, setPendingCount] = useState(0);

  const refreshPending = React.useCallback(() => {
    if (user?.role !== "master") return;
    api.get("/admin/pending").then((r) => setPendingCount(r.data.length)).catch(() => {});
  }, [user]);

  useEffect(() => { refreshPending(); }, [refreshPending]);

  const onReviewChange = () => { reload(); refreshPending(); };
  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-page">
      <header className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <div>
            <div className="font-heading text-xl font-medium">Te Manawa · Admin</div>
            <div className="text-xs text-white/50 flex items-center gap-2">
              {user?.email}
              <span className="inline-flex items-center gap-1 bg-gold/20 text-gold px-2 py-0.5 rounded-sm">
                {isMaster && <ShieldCheck size={12} weight="fill" />}{roleLabel(user?.role)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="text-sm text-white/70 hover:text-gold">View site ↗</a>
            <button onClick={doLogout} data-testid="logout-btn" className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 rounded-sm text-sm hover:bg-white/10 transition-colors">
              <SignOut size={16} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <Tabs defaultValue="content">
          <TabsList className="bg-white border border-navy/10 rounded-none flex-wrap h-auto">
            <TabsTrigger value="content" data-testid="tab-content">Content & Images</TabsTrigger>
            <TabsTrigger value="pages" data-testid="tab-pages">Events & News</TabsTrigger>
            <TabsTrigger value="policies" data-testid="tab-policies">Policies</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries</TabsTrigger>
            {isMaster && (
              <TabsTrigger value="review" data-testid="tab-review" className="relative">
                Approvals
                {pendingCount > 0 && (
                  <span data-testid="pending-badge" className="ml-2 inline-grid place-items-center min-w-5 h-5 px-1.5 text-xs font-bold rounded-full bg-gold text-navy">
                    {pendingCount}
                  </span>
                )}
              </TabsTrigger>
            )}
            {isMaster && <TabsTrigger value="players" data-testid="tab-players">NZ Player Database</TabsTrigger>}
            {isMaster && <TabsTrigger value="teams" data-testid="tab-teams">Team Entry</TabsTrigger>}
            {isMaster && <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>}
            {isMaster && <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>}
            {isMaster && <TabsTrigger value="audit" data-testid="tab-audit">Activity Log</TabsTrigger>}
          </TabsList>
          <TabsContent value="content" className="mt-6"><ContentEditor /></TabsContent>
          <TabsContent value="pages" className="mt-6"><PagesManager /></TabsContent>
          <TabsContent value="policies" className="mt-6"><PoliciesPanel /></TabsContent>
          <TabsContent value="enquiries" className="mt-6"><Enquiries /></TabsContent>
          {isMaster && <TabsContent value="review" className="mt-6"><ReviewPanel onChange={onReviewChange} /></TabsContent>}
          {isMaster && <TabsContent value="players" className="mt-6"><PlayersPanel /></TabsContent>}
          {isMaster && <TabsContent value="teams" className="mt-6"><TeamEntryPanel /></TabsContent>}
          {isMaster && <TabsContent value="users" className="mt-6"><UsersPanel /></TabsContent>}
          {isMaster && <TabsContent value="history" className="mt-6"><HistoryPanel onChange={reload} /></TabsContent>}
          {isMaster && <TabsContent value="audit" className="mt-6"><AuditPanel /></TabsContent>}
        </Tabs>
      </main>
    </div>
  );
}
